import React, { useEffect, useState, useRef, useCallback } from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { PageShell, PageHeader, Surface, ActionButton, StatePanel, Empty } from '../components/ui';
import { hasAccess } from '../utils/permissions';
import {
    DEFAULT_REFERRAL_SETTINGS,
    REFERRAL_NUMERIC_FIELDS,
    INERT_FIELD_NOTE,
    normalizeReferralSettings,
    toReferralDraft,
    buildReferralPatch,
    formatFieldValue,
    parseFieldText,
} from '../utils/referralSettings';

const API = 'https://skilzapp.net/api/platformPricing';
const EDIT_AUTH_API = 'https://skilzapp.net/api/platformPricing/edit-authorization';
const EDIT_TOKEN_HEADER = 'X-Pricing-Edit-Token';

// ── Shared inline styles ─────────────────────────────────────────────────────

const styles = {
    input: {
        width: '100%',
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid #e7eaf0',
        fontSize: 14,
        fontFamily: 'inherit',
        boxSizing: 'border-box',
    },
    label: { fontSize: 12, fontWeight: 600, color: '#566071', marginBottom: 4, display: 'block' },
    planCard: {
        padding: 16,
        borderRadius: 12,
        border: '1px solid #e7eaf0',
        marginBottom: 12,
        background: '#fafbfc',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 14,
    },
    th: {
        textAlign: 'left',
        padding: '8px 12px',
        borderBottom: '2px solid #e7eaf0',
        fontSize: 12,
        fontWeight: 600,
        color: '#566071',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
    td: {
        padding: '8px 12px',
        borderBottom: '1px solid #eef1f5',
    },
    tdInput: {
        width: 72,
        padding: '4px 8px',
        borderRadius: 8,
        border: '1px solid #e7eaf0',
        fontSize: 14,
        textAlign: 'right',
    },
    row: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        marginBottom: 8,
    },
    field: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    success: {
        padding: '8px 16px',
        borderRadius: 8,
        background: '#dff9eb',
        color: '#096844',
        fontSize: 14,
        marginBottom: 12,
    },
    error: {
        padding: '8px 16px',
        borderRadius: 8,
        background: '#ffeef5',
        color: '#b3145a',
        fontSize: 14,
        marginBottom: 12,
    },
    featureRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 4,
    },
    roValue: {
        padding: '8px 12px',
        borderRadius: 8,
        border: '1px solid #eef1f5',
        fontSize: 14,
        color: '#14161f',
        background: '#f9fafb',
        minHeight: 37,
        display: 'flex',
        alignItems: 'center',
    },
    lockBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 12px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        userSelect: 'none',
    },
    lockBadgeLocked: {
        background: '#f0eaff',
        color: '#4e299c',
    },
    lockBadgeUnlocked: {
        background: '#dff9eb',
        color: '#096844',
    },
    modalOverlay: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(7,11,22,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
    },
    modalCard: {
        background: '#fff',
        borderRadius: 20,
        boxShadow: '0 18px 48px rgba(20,22,31,.16)',
        width: '100%',
        maxWidth: 400,
        padding: 28,
    },
    modalTitle: {
        fontFamily: "'Bricolage Grotesque', system-ui, sans-serif",
        fontSize: 21,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        margin: '0 0 6px',
    },
    modalDesc: {
        color: '#566071',
        fontSize: 13,
        marginBottom: 20,
    },
    pinInput: {
        width: '100%',
        padding: '10px 14px',
        borderRadius: 8,
        border: '1px solid #e7eaf0',
        fontSize: 18,
        fontFamily: 'inherit',
        letterSpacing: '.3em',
        textAlign: 'center',
    },
    pinError: {
        color: '#b3145a',
        fontSize: 12.5,
        fontWeight: 600,
        marginTop: 2,
        minHeight: 18,
    },
    modalActions: {
        display: 'flex',
        gap: 10,
        justifyContent: 'flex-end',
    },
};

// ── Lock Icon SVG ────────────────────────────────────────────────────────────

function LockIcon({ unlocked }) {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            {unlocked
                ? <path d="M7 11V7a5 5 0 0 1 5-5 5 5 0 0 1 5 5" />
                : <path d="M7 11V7a5 5 0 0 1 10 0v4" />}
        </svg>
    );
}

// ── Read-only displays ───────────────────────────────────────────────────────

function ReadOnlyValue({ value }) {
    return <div style={styles.roValue}>{value}</div>;
}

// ── Plan Editor / Read-Only ──────────────────────────────────────────────────

function PlanEditor({ plans, onChange, readOnly }) {
    const update = (idx, key, val) => {
        const next = plans.map((p, i) => (i === idx ? { ...p, [key]: val } : p));
        onChange(next);
    };

    const updateFeature = (planIdx, featIdx, val) => {
        const next = plans.map((p, i) => {
            if (i !== planIdx) return p;
            const feats = [...p.features];
            feats[featIdx] = val;
            return { ...p, features: feats };
        });
        onChange(next);
    };

    const addFeature = (planIdx) => {
        const next = plans.map((p, i) =>
            i === planIdx ? { ...p, features: [...p.features, ''] } : p
        );
        onChange(next);
    };

    const removeFeature = (planIdx, featIdx) => {
        const next = plans.map((p, i) => {
            if (i !== planIdx) return p;
            const feats = p.features.filter((_, j) => j !== featIdx);
            return { ...p, features: feats };
        });
        onChange(next);
    };

    return (
        <div>
            {plans.length === 0 && <Empty>No subscription plans configured.</Empty>}
            {plans.map((plan, idx) => (
                <div key={plan.id} style={styles.planCard}>
                    <div style={{ ...styles.row, marginBottom: 12 }}>
                        <strong style={{ fontSize: 16 }}>{plan.id}</strong>
                    </div>
                    <div style={styles.row}>
                        <div style={styles.field}>
                            <label style={styles.label}>Name</label>
                            {readOnly
                                ? <ReadOnlyValue value={plan.name} />
                                : <input style={styles.input} value={plan.name} onChange={e => update(idx, 'name', e.target.value)} />}
                        </div>
                        <div style={styles.field}>
                            <label style={styles.label}>Price (cents)</label>
                            {readOnly
                                ? <ReadOnlyValue value={plan.price} />
                                : <input style={styles.input} type="number" value={plan.price} onChange={e => update(idx, 'price', Number(e.target.value))} />}
                        </div>
                        <div style={styles.field}>
                            <label style={styles.label}>Monthly Price ($)</label>
                            {readOnly
                                ? <ReadOnlyValue value={plan.monthlyPrice} />
                                : <input style={styles.input} type="number" step="0.01" value={plan.monthlyPrice} onChange={e => update(idx, 'monthlyPrice', Number(e.target.value))} />}
                        </div>
                        <div style={styles.field}>
                            <label htmlFor={`included-skill-slots-${idx}`} style={styles.label}>Included skill slots</label>
                            {readOnly
                                ? <ReadOnlyValue value={plan.includedSkillSlots} />
                                : <input
                                    id={`included-skill-slots-${idx}`}
                                    aria-label="Included skill slots"
                                    style={styles.input}
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={plan.includedSkillSlots}
                                    onChange={e => update(idx, 'includedSkillSlots', Number(e.target.value))}
                                />}
                        </div>
                    </div>
                    <label style={styles.label}>Features</label>
                    {readOnly ? (
                        plan.features.map((feat, fi) => (
                            <div key={fi} style={styles.featureRow}>
                                <span style={{ color: '#4de68c', fontWeight: 800 }}>•</span>
                                <span style={{ fontSize: 13 }}>{feat}</span>
                            </div>
                        ))
                    ) : (
                        <>
                            {plan.features.map((feat, fi) => (
                                <div key={fi} style={styles.featureRow}>
                                    <input style={{ ...styles.input, flex: 1 }} value={feat} onChange={e => updateFeature(idx, fi, e.target.value)} />
                                    <button onClick={() => removeFeature(idx, fi)} style={{ background: 'none', border: 'none', color: '#b3145a', cursor: 'pointer', fontSize: 18 }}>×</button>
                                </div>
                            ))}
                            <button onClick={() => addFeature(idx)} style={{ background: 'none', border: 'none', color: '#7c3aed', cursor: 'pointer', fontSize: 13, marginTop: 4 }}>+ Add feature</button>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
}

// ── Bracket Table ────────────────────────────────────────────────────────────

function BracketTable({ brackets, onChange, readOnly }) {
    const tiers = ['FREE', 'PLUS', 'PRO', 'BUSINESS'];

    const updateRate = (bracketIdx, tier, val) => {
        const next = brackets.map((b, i) => {
            if (i !== bracketIdx) return b;
            return { ...b, rates: { ...b.rates, [tier]: Number(val) } };
        });
        onChange(next);
    };

    const updateField = (bracketIdx, field, val) => {
        const next = brackets.map((b, i) =>
            i === bracketIdx ? { ...b, [field]: val === '' ? null : Number(val) } : b
        );
        onChange(next);
    };

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.th}>Min (¢)</th>
                        <th style={styles.th}>Max (¢)</th>
                        {tiers.map(t => <th key={t} style={styles.th}>{t} (bps)</th>)}
                    </tr>
                </thead>
                <tbody>
                    {brackets.length === 0 && (
                        <tr><td colSpan={2 + tiers.length} style={{ ...styles.td, color: '#7f8999', textAlign: 'center' }}>No fee brackets configured.</td></tr>
                    )}
                    {brackets.map((b, idx) => (
                        <tr key={idx}>
                            <td style={styles.td}>
                                {readOnly
                                    ? <span>{b.minAmount}</span>
                                    : <input style={styles.tdInput} type="number" value={b.minAmount} onChange={e => updateField(idx, 'minAmount', e.target.value)} />}
                            </td>
                            <td style={styles.td}>
                                {readOnly
                                    ? <span>{b.maxAmount == null ? '∞' : b.maxAmount}</span>
                                    : <input style={styles.tdInput} type="number" value={b.maxAmount == null ? '' : b.maxAmount} placeholder="∞" onChange={e => updateField(idx, 'maxAmount', e.target.value)} />}
                            </td>
                            {tiers.map(t => (
                                <td key={t} style={styles.td}>
                                    {readOnly
                                        ? <span>{b.rates[t]}</span>
                                        : <input style={styles.tdInput} type="number" value={b.rates[t]} onChange={e => updateRate(idx, t, e.target.value)} />}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Tax Settings ─────────────────────────────────────────────────────────────

function TaxSettings({ settings, onChange, readOnly }) {
    return (
        <div style={styles.row}>
            <div style={styles.field}>
                <label style={styles.label}>Lebanon Withholding Rate (bps)</label>
                {readOnly
                    ? <ReadOnlyValue value={settings.lebanonWithholdingBps} />
                    : <input
                        style={styles.input}
                        type="number"
                        value={settings.lebanonWithholdingBps}
                        onChange={e => onChange({ ...settings, lebanonWithholdingBps: Number(e.target.value) })}
                    />}
                <span style={{ fontSize: 12, color: '#7f8999', marginTop: 4 }}>
                    {(settings.lebanonWithholdingBps / 100).toFixed(2)}%
                </span>
            </div>
        </div>
    );
}

// ── Referral Rewards (Skilz Circle) ──────────────────────────────────────────

const referralStyles = {
    killSwitchOff: {
        padding: '12px 16px',
        borderRadius: 12,
        background: '#f9fafb',
        border: '1px solid #e7eaf0',
        color: '#14161f',
        fontSize: 14,
        fontWeight: 600,
    },
    killSwitchOn: {
        padding: '12px 16px',
        borderRadius: 12,
        background: '#dff9eb',
        border: '1px solid #4de68c',
        color: '#096844',
        fontSize: 14,
        fontWeight: 600,
    },
    pending: {
        fontSize: 12.5,
        fontWeight: 700,
        color: '#9a3d13',
        marginTop: 6,
    },
    help: { fontSize: 12, color: '#7f8999', marginTop: 4 },
    inert: {
        fontSize: 12,
        fontWeight: 700,
        color: '#9a3d13',
        marginTop: 4,
    },
    fieldError: {
        color: '#b3145a',
        fontSize: 12.5,
        fontWeight: 600,
        marginTop: 4,
    },
};

const KILL_SWITCH_OFF_TEXT = 'Referral rewards are OFF — no rewards are earned and the app shows the feature as unavailable.';
const KILL_SWITCH_ON_TEXT = 'Referral rewards are ON — completed deals by referred providers earn rewards for their inviters.';

function ReferralEnableConfirm({ open, onCancel, onConfirm }) {
    const cancelRef = useRef(null);

    useEffect(() => {
        if (open) {
            Promise.resolve().then(() => {
                if (cancelRef.current) cancelRef.current.focus();
            });
        }
    }, [open]);

    if (!open) return null;

    return (
        <div
            style={styles.modalOverlay}
            onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
            onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
            role="dialog"
            aria-modal="true"
            aria-label="Enable referral rewards"
            data-testid="referral-enable-confirm"
        >
            <div style={{ ...styles.modalCard, maxWidth: 460 }}>
                <h3 style={styles.modalTitle}>Enable referral rewards in production?</h3>
                <p style={styles.modalDesc}>
                    This edits the <strong>production</strong> database. Once you click Save Changes,
                    enabling takes effect in production immediately: referral rewards will begin
                    accruing on completed deals, using the percentage and caps on this page.
                </p>
                <div style={styles.modalActions}>
                    <button
                        ref={cancelRef}
                        type="button"
                        className="sk-action sk-action-secondary"
                        onClick={onCancel}
                        data-testid="referral-enable-cancel"
                    >
                        Keep OFF
                    </button>
                    <ActionButton type="button" onClick={onConfirm} data-testid="referral-enable-confirm-btn">
                        Yes, enable referral rewards
                    </ActionButton>
                </div>
            </div>
        </div>
    );
}

function ReferralSettings({ draft, saved, errors, onChange, readOnly }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const savedEnabled = saved.rewardsEnabled === true;
    const shownEnabled = readOnly ? savedEnabled : draft.rewardsEnabled === true;

    const setField = (key, value) => onChange(key, value);

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <label style={styles.label}>Kill switch</label>
                <div
                    style={shownEnabled ? referralStyles.killSwitchOn : referralStyles.killSwitchOff}
                    data-testid="referral-kill-switch-status"
                    role="status"
                >
                    {shownEnabled ? KILL_SWITCH_ON_TEXT : KILL_SWITCH_OFF_TEXT}
                </div>
                {!readOnly && (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        {draft.rewardsEnabled === true ? (
                            <ActionButton
                                type="button"
                                variant="secondary"
                                onClick={() => setField('rewardsEnabled', false)}
                                data-testid="referral-disable-btn"
                            >
                                Turn referral rewards OFF
                            </ActionButton>
                        ) : (
                            <ActionButton
                                type="button"
                                onClick={() => setConfirmOpen(true)}
                                data-testid="referral-enable-btn"
                            >
                                Turn referral rewards ON…
                            </ActionButton>
                        )}
                        {draft.rewardsEnabled !== savedEnabled && (
                            <div style={referralStyles.pending} data-testid="referral-kill-switch-pending">
                                Unsaved: referral rewards will be turned {draft.rewardsEnabled ? 'ON' : 'OFF'} when you click Save Changes.
                            </div>
                        )}
                    </div>
                )}
                {errors.rewardsEnabled && (
                    <div style={referralStyles.fieldError} role="alert" data-testid="referral-error-rewardsEnabled">
                        {errors.rewardsEnabled}
                    </div>
                )}
            </div>

            <div style={styles.row}>
                {REFERRAL_NUMERIC_FIELDS.map(f => {
                    const inputId = `referral-${f.key}`;
                    const errorId = `referral-error-${f.key}`;
                    const err = errors[f.key];
                    return (
                        <div key={f.key} style={styles.field}>
                            <label htmlFor={inputId} style={styles.label}>{f.label}</label>
                            {readOnly ? (
                                <div data-testid={`referral-value-${f.key}`}>
                                    <ReadOnlyValue value={formatFieldValue(f.key, saved[f.key])} />
                                </div>
                            ) : (
                                <input
                                    id={inputId}
                                    style={{ ...styles.input, borderColor: err ? '#b3145a' : '#e7eaf0' }}
                                    type="text"
                                    inputMode={f.unit === 'int' ? 'numeric' : 'decimal'}
                                    value={draft[f.key]}
                                    aria-invalid={err ? 'true' : 'false'}
                                    aria-describedby={err ? errorId : undefined}
                                    onChange={e => setField(f.key, e.target.value)}
                                />
                            )}
                            {!readOnly && !err && parseFieldText(f.key, draft[f.key]).value !== undefined && f.unit !== 'int' && (
                                <span style={referralStyles.help}>
                                    = {formatFieldValue(f.key, parseFieldText(f.key, draft[f.key]).value)}
                                </span>
                            )}
                            <span style={referralStyles.help}>{f.help}</span>
                            {f.inert && <span style={referralStyles.inert} data-testid={`referral-inert-${f.key}`}>{INERT_FIELD_NOTE}</span>}
                            {err && (
                                <div id={errorId} style={referralStyles.fieldError} role="alert" data-testid={errorId}>
                                    {err}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <ReferralEnableConfirm
                open={confirmOpen}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={() => {
                    setConfirmOpen(false);
                    setField('rewardsEnabled', true);
                }}
            />
        </div>
    );
}

// ── PIN Modal ────────────────────────────────────────────────────────────────

function PinModal({ open, onClose, onUnlock, loading }) {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setPin('');
            setError('');
            // Focus immediately via microtask (no arbitrary delay)
            Promise.resolve().then(() => {
                if (inputRef.current) inputRef.current.focus();
            });
        }
    }, [open]);

    const handleSubmit = useCallback(() => {
        if (!/^\d{4}$/.test(pin)) {
            setError('Please enter a valid 4-digit PIN.');
            return;
        }
        setError('');
        onUnlock(pin, (errMsg) => {
            setPin('');
            setError(errMsg);
            if (inputRef.current) inputRef.current.focus();
        });
    }, [pin, onUnlock]);

    const handleOverlayClick = useCallback((e) => {
        if (e.target === e.currentTarget) onClose();
    }, [onClose]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') { handleSubmit(); return; }
        if (e.key === 'Escape' && !loading) { onClose(); return; }
        // Tab containment within dialog
        if (e.key === 'Tab') {
            const modal = e.currentTarget.querySelector('[data-testid="pin-modal"] > div') || e.currentTarget;
            const focusable = modal.querySelectorAll('input, button, [tabindex]:not([tabindex="-1"])');
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }, [handleSubmit, loading, onClose]);

    if (!open) return null;

    return (
        <div
            style={styles.modalOverlay}
            onClick={handleOverlayClick}
            onKeyDown={handleKeyDown}
            role="dialog"
            aria-modal="true"
            aria-label="Unlock Editing"
            data-testid="pin-modal"
        >
            <div style={styles.modalCard}>
                <h3 style={styles.modalTitle}>🔒 Unlock Editing</h3>
                <p style={styles.modalDesc}>Enter your 4-digit edit PIN to modify pricing settings.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                    <label htmlFor="pinInput" style={{ fontSize: 12, fontWeight: 700, color: '#566071' }}>Edit PIN</label>
                    <input
                        ref={inputRef}
                        id="pinInput"
                        type="password"
                        maxLength="4"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="••••"
                        autoComplete="off"
                        value={pin}
                        onChange={e => { setPin(e.target.value); setError(''); }}
                        style={{
                            ...styles.pinInput,
                            borderColor: error ? '#b3145a' : '#e7eaf0',
                            boxShadow: error ? '0 0 0 3px rgba(179,20,90,.12)' : undefined,
                        }}
                        data-testid="pin-input"
                    />
                    <div style={styles.pinError} data-testid="pin-error" role="alert">{error}</div>
                </div>
                <div style={styles.modalActions}>
                    <ActionButton variant="secondary" onClick={onClose} disabled={loading}>Cancel</ActionButton>
                    <ActionButton onClick={handleSubmit} disabled={loading} data-testid="unlock-btn">
                        {loading ? 'Verifying…' : 'Unlock Editing'}
                    </ActionButton>
                </div>
            </div>
        </div>
    );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function Pricing() {
    const { permissions } = usePermissions();
    const adminRoles = (permissions && permissions.adminRoles) || [];

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [editing, setEditing] = useState(false);
    const [pinModalOpen, setPinModalOpen] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const [plans, setPlans] = useState([]);
    const [feeSchedule, setFeeSchedule] = useState({ version: '', currency: 'USD', brackets: [] });
    const [taxSettings, setTaxSettings] = useState({ lebanonWithholdingBps: 825 });
    // Referral settings: `referralSaved` is in backend units (last saved/loaded),
    // `referralDraft` holds the edit-input texts + the kill-switch boolean.
    const [referralSaved, setReferralSaved] = useState(DEFAULT_REFERRAL_SETTINGS);
    const [referralDraft, setReferralDraft] = useState(() => toReferralDraft(DEFAULT_REFERRAL_SETTINGS));
    const [referralErrors, setReferralErrors] = useState({});

    // Snapshot of last saved data for cancel restore
    const savedSnapshot = useRef(null);
    // Edit token — component memory only
    const editTokenRef = useRef(null);

    const clearEditState = useCallback(() => {
        editTokenRef.current = null;
        setEditing(false);
    }, []);

    useEffect(() => {
        setLoading(true);
        httpClient(API)
            .then(({ json }) => {
                const snapshot = {
                    plans: json.plans || [],
                    feeSchedule: json.feeSchedule || { version: '', currency: 'USD', brackets: [] },
                    taxSettings: json.taxSettings || { lebanonWithholdingBps: 825 },
                    // Absent/partial/invalid subdocument (old documents) falls
                    // back per field to the backend defaults; kill switch OFF.
                    referral: normalizeReferralSettings(json.referral),
                };
                setPlans(snapshot.plans);
                setFeeSchedule(snapshot.feeSchedule);
                setTaxSettings(snapshot.taxSettings);
                setReferralSaved(snapshot.referral);
                setReferralDraft(toReferralDraft(snapshot.referral));
                setReferralErrors({});
                savedSnapshot.current = JSON.parse(JSON.stringify(snapshot));
            })
            .catch(err => setMsg({ type: 'error', text: 'Failed to load pricing: ' + (err.message || err) }))
            .finally(() => setLoading(false));

        // Cleanup: clear token on unmount
        return () => { editTokenRef.current = null; };
    }, []);

    // Safe error-code extractor: handles object, valid JSON string, malformed text, absent body
    const extractErrorCode = useCallback((err) => {
        try {
            const body = err.body
                ? (typeof err.body === 'string' ? JSON.parse(err.body) : err.body)
                : {};
            return body?.errors?.msg || null;
        } catch {
            return null;
        }
    }, []);

    // Per-field errors from a 422 INVALID_REFERRAL_SETTINGS body ({ errors: { fields } }).
    const extractErrorFields = useCallback((err) => {
        try {
            const body = err.body
                ? (typeof err.body === 'string' ? JSON.parse(err.body) : err.body)
                : {};
            const fields = body?.errors?.fields;
            if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return {};
            const out = {};
            for (const [k, v] of Object.entries(fields)) {
                if (typeof v === 'string') out[k] = v;
            }
            return out;
        } catch {
            return {};
        }
    }, []);

    const handleUnlock = useCallback((pin, onError) => {
        setVerifying(true);
        httpClient(EDIT_AUTH_API, {
            method: 'POST',
            body: JSON.stringify({ pin }),
        })
            .then(({ json }) => {
                // Finding 8: Missing/empty editToken must not unlock
                if (!json.editToken) {
                    onError('Authorization failed. Please try again.');
                    return;
                }
                editTokenRef.current = json.editToken;
                setPinModalOpen(false);
                setEditing(true);
                setMsg(null);
            })
            .catch(err => {
                editTokenRef.current = null;
                const code = extractErrorCode(err);
                if (code === 'TOO_MANY_REQUESTS') {
                    onError('Too many attempts. Please try again later.');
                } else if (code === 'SERVICE_UNAVAILABLE') {
                    onError('Service temporarily unavailable. Please try again later.');
                } else {
                    onError('Incorrect PIN. Please try again.');
                }
            })
            .finally(() => setVerifying(false));
    }, []);

    const restoreReferralFromSnapshot = useCallback(() => {
        if (savedSnapshot.current && savedSnapshot.current.referral) {
            const referral = JSON.parse(JSON.stringify(savedSnapshot.current.referral));
            setReferralSaved(referral);
            setReferralDraft(toReferralDraft(referral));
        }
        setReferralErrors({});
    }, []);

    const handleReferralChange = useCallback((key, value) => {
        setReferralDraft(prev => ({ ...prev, [key]: value }));
        setReferralErrors(prev => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    const handleCancel = useCallback(() => {
        if (savedSnapshot.current) {
            setPlans(JSON.parse(JSON.stringify(savedSnapshot.current.plans)));
            setFeeSchedule(JSON.parse(JSON.stringify(savedSnapshot.current.feeSchedule)));
            setTaxSettings(JSON.parse(JSON.stringify(savedSnapshot.current.taxSettings)));
        }
        restoreReferralFromSnapshot();
        setMsg(null);
        clearEditState();
    }, [clearEditState, restoreReferralFromSnapshot]);

    const handleSave = useCallback(() => {
        if (plans.some(plan => !Number.isInteger(plan.includedSkillSlots) || plan.includedSkillSlots < 0)) {
            setMsg({ type: 'error', text: 'Included skill slots must be a nonnegative integer.' });
            return;
        }
        // Referral settings: strict client-side validation (mirrors the
        // backend's non-coercing validator) and a patch of changed keys only.
        const { patch: referralPatch, errors: referralValidationErrors } = buildReferralPatch(referralDraft, referralSaved);
        if (Object.keys(referralValidationErrors).length > 0) {
            setReferralErrors(referralValidationErrors);
            setMsg({ type: 'error', text: 'Fix the highlighted referral settings before saving.' });
            return;
        }
        setReferralErrors({});
        const hasReferralChanges = Object.keys(referralPatch).length > 0;
        setSaving(true);
        setMsg(null);

        httpClient(API, {
            method: 'PATCH',
            body: JSON.stringify({
                plans,
                feeSchedule,
                taxSettings,
                ...(hasReferralChanges ? { referral: referralPatch } : {}),
            }),
            headers: new Headers({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                [EDIT_TOKEN_HEADER]: editTokenRef.current || '',
            }),
        })
            .then(({ json }) => {
                const snapshot = {
                    plans: json.plans || plans,
                    feeSchedule: json.feeSchedule || feeSchedule,
                    taxSettings: json.taxSettings || taxSettings,
                    referral: json.referral
                        ? normalizeReferralSettings(json.referral)
                        : { ...referralSaved, ...referralPatch },
                };
                setPlans(snapshot.plans);
                setFeeSchedule(snapshot.feeSchedule);
                setTaxSettings(snapshot.taxSettings);
                setReferralSaved(snapshot.referral);
                setReferralDraft(toReferralDraft(snapshot.referral));
                setReferralErrors({});
                savedSnapshot.current = JSON.parse(JSON.stringify(snapshot));
                setMsg({ type: 'success', text: 'Pricing saved successfully.' });
                clearEditState();
            })
            .catch(err => {
                const code = extractErrorCode(err);
                if (code === 'EDIT_TOKEN_EXPIRED' || code === 'INVALID_EDIT_TOKEN' || code === 'MISSING_EDIT_TOKEN') {
                    // Auth failure: restore saved snapshot, clear token, relock
                    if (savedSnapshot.current) {
                        setPlans(JSON.parse(JSON.stringify(savedSnapshot.current.plans)));
                        setFeeSchedule(JSON.parse(JSON.stringify(savedSnapshot.current.feeSchedule)));
                        setTaxSettings(JSON.parse(JSON.stringify(savedSnapshot.current.taxSettings)));
                    }
                    restoreReferralFromSnapshot();
                    clearEditState();
                    setMsg({ type: 'error', text: 'Your editing session has expired. Please unlock again to continue.' });
                } else if (code === 'INVALID_REFERRAL_SETTINGS') {
                    // Backend 422: per-field messages; nothing was written. Remain editable.
                    const fields = extractErrorFields(err);
                    setReferralErrors(fields);
                    setMsg({ type: 'error', text: 'The server rejected some referral settings — see the highlighted fields. Nothing was saved.' });
                } else {
                    // Non-auth failure: remain editable
                    setMsg({ type: 'error', text: 'Save failed: ' + (err.message || err) });
                }
            })
            .finally(() => setSaving(false));
    }, [plans, feeSchedule, taxSettings, referralDraft, referralSaved, clearEditState, extractErrorCode, extractErrorFields, restoreReferralFromSnapshot]);

    if (!hasAccess('pricing', adminRoles)) {
        return (
            <PageShell>
                <StatePanel state="error">You do not have permission to view this page.</StatePanel>
            </PageShell>
        );
    }

    if (loading) {
        return (
            <PageShell>
                <StatePanel state="loading">Loading pricing configuration…</StatePanel>
            </PageShell>
        );
    }

    const headerActions = editing ? (
        <>
            <ActionButton variant="secondary" onClick={handleCancel} disabled={saving}>Cancel</ActionButton>
            <ActionButton onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save Changes'}
            </ActionButton>
        </>
    ) : (
        <ActionButton onClick={() => setPinModalOpen(true)}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <LockIcon unlocked={false} />
                Edit Pricing
            </span>
        </ActionButton>
    );

    const lockBadge = (
        <span
            style={{
                ...styles.lockBadge,
                ...(editing ? styles.lockBadgeUnlocked : styles.lockBadgeLocked),
            }}
            data-testid="lock-badge"
        >
            <LockIcon unlocked={editing} />
            {editing ? 'Editing' : 'Read only'}
        </span>
    );

    return (
        <PageShell>
            <PageHeader
                title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        Platform Pricing
                        {lockBadge}
                    </span>
                }
                description="Manage subscription plans, fee brackets, tax settings, and referral rewards."
                actions={headerActions}
            />

            {msg && <div style={msg.type === 'success' ? styles.success : styles.error} role="status" data-testid="page-message">{msg.text}</div>}

            <Surface title="Subscription Plans" description="Plan names, prices, and features shown to users.">
                <PlanEditor plans={plans} onChange={setPlans} readOnly={!editing} />
            </Surface>

            <Surface title="Fee Brackets" description="Total fee rates in basis points per subscription tier per amount bracket. Empty max = ∞.">
                <div style={{ ...styles.row, marginBottom: 12 }}>
                    <div style={styles.field}>
                        <label style={styles.label}>Version</label>
                        {!editing
                            ? <ReadOnlyValue value={feeSchedule.version} />
                            : <input style={styles.input} value={feeSchedule.version} onChange={e => setFeeSchedule({ ...feeSchedule, version: e.target.value })} />}
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>Currency</label>
                        {!editing
                            ? <ReadOnlyValue value={feeSchedule.currency} />
                            : <input style={styles.input} value={feeSchedule.currency} onChange={e => setFeeSchedule({ ...feeSchedule, currency: e.target.value })} />}
                    </div>
                </div>
                <BracketTable brackets={feeSchedule.brackets} onChange={brackets => setFeeSchedule({ ...feeSchedule, brackets })} readOnly={!editing} />
            </Surface>

            <Surface title="Tax Settings" description="Withholding configuration for specific regions.">
                <TaxSettings settings={taxSettings} onChange={setTaxSettings} readOnly={!editing} />
            </Surface>

            <Surface
                title="Referral Rewards"
                description="Skilz Circle — inviter rewards on referred providers' completed deals. Changes apply to the production database when saved."
            >
                <ReferralSettings
                    draft={referralDraft}
                    saved={referralSaved}
                    errors={referralErrors}
                    onChange={handleReferralChange}
                    readOnly={!editing}
                />
            </Surface>

            <PinModal
                open={pinModalOpen}
                onClose={() => setPinModalOpen(false)}
                onUnlock={handleUnlock}
                loading={verifying}
            />
        </PageShell>
    );
}

// Export internals for testing
export { PinModal, PlanEditor, BracketTable, TaxSettings, ReferralSettings };
